/**
 * AppInfoSection - App information component
 *
 * Pure presentational component showing app version, privacy, and terms links.
 * All handlers in parent.
 */
import { View } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { TierBody } from "@dramadetector/ui";
import { GlassCard } from "../../../components/glass";
import { SettingsItem } from "../../../components/SettingsItem";

interface AppInfoSectionProps {
  /** App version string */
  appVersion: string;
  /** About callback */
  onAbout: () => void;
  /** Privacy callback */
  onPrivacy: () => void;
  /** Terms callback */
  onTerms: () => void;
  /** Mobile app request callback (Mobile Demand Signal) */
  onRequestMobileApp?: () => void;
}

export function AppInfoSection({
  appVersion,
  onAbout,
  onPrivacy,
  onTerms,
  onRequestMobileApp,
}: AppInfoSectionProps) {
  const { t } = useTranslation('settings');
  const { t: tMobileApp } = useTranslation('mobile_app');

  return (
    <View className="mt-6">
      <TierBody size="xs" weight="semibold" className="text-gray-500 uppercase px-4 mb-2">
        {t('sections.app_info')}
      </TierBody>
      <GlassCard>
        <SettingsItem
          icon="ℹ️"
          label={t('about.title')}
          value={appVersion}
          onPress={onAbout}
        />
        <View className="border-t border-gray-200" />
        <SettingsItem
          icon="🔒"
          label={t('privacy.title')}
          onPress={onPrivacy}
        />
        <View className="border-t border-gray-200" />
        <SettingsItem
          icon="📄"
          label={t('terms.title')}
          onPress={onTerms}
        />
        {/* Mobile Demand Signal: Track user interest in native app */}
        {onRequestMobileApp && (
          <>
            <View className="border-t border-gray-200" />
            <SettingsItem
              icon="📱"
              label={tMobileApp('title')}
              onPress={onRequestMobileApp}
            />
          </>
        )}
      </GlassCard>
    </View>
  );
}
